import React, { useEffect, useMemo, useRef, useState } from "react";
import { sfxMomentId, sfxMomentTimelineMs, timelineDuration, type Clip } from "@hudhud/core";
import { Icon } from "../components/Icon.js";
import { Slider } from "../components/Controls.js";
import { useToast } from "../components/Toast.js";
import { usePhone } from "../hooks/useMedia.js";
import { api, type MusicTrack, type SfxSound, type SoundPeaks } from "../api.js";
import { useSfxLibrary, reloadSfx } from "../components/SfxPick.js";
import { useEditor } from "./store.js";

export type SoundTarget = "opener" | "endCard" | "cuts" | "playhead";
export const SOUND_TARGETS: { value: SoundTarget; label: string; hint: string }[] = [
  { value: "opener", label: "Opener", hint: "as the opener appears" },
  { value: "endCard", label: "End card", hint: "as the end card lands" },
  { value: "cuts", label: "Every cut", hint: "at each cut after the first piece" },
  { value: "playhead", label: "At playhead", hint: "where the preview is paused" },
];
export const MOOD_LABEL: Record<string, string> = { calm: "Calm", uplifting: "Uplifting", meditative: "Meditative", warm: "Warm", steady: "Steady", solemn: "Solemn", traditional: "Traditional", uploaded: "My uploads" };
export const GROUP_LABEL: Record<string, string> = { transition: "Whooshes", hit: "Hits", ui: "Small sounds", texture: "Textures", uploaded: "My sounds" };
type Kind = "all" | "beds" | "effects" | "starred";
const HOVER_DELAY_MS = 220;

/* ---------- one preview player for the whole studio: hearing a sound stops the one before ---------- */
let previewEl: HTMLAudioElement | null = null;
let previewUrl: string | null = null;
const previewListeners = new Set<(url: string | null, progress: number) => void>();
const tellPreview = (progress: number) => previewListeners.forEach((l) => l(previewUrl, progress));
/** Plays one sound from `startMs` (a bed from its strongest stretch); null stops whatever is playing. */
export function previewSound(url: string | null, startMs = 0): void {
  if (previewEl) { previewEl.pause(); previewEl.ontimeupdate = null; previewEl.onended = null; previewEl = null; }
  previewUrl = url;
  if (!url) { tellPreview(0); return; }
  const a = new Audio(url);
  a.volume = 0.7;
  const seek = () => { try { a.currentTime = startMs / 1000; } catch { /* not seekable yet */ } };
  if (a.readyState >= 1) seek(); else a.onloadedmetadata = seek;
  a.ontimeupdate = () => { if (previewEl === a && a.duration) tellPreview(a.currentTime / a.duration); };
  a.onended = () => { if (previewEl === a) { previewEl = null; previewUrl = null; tellPreview(0); } };
  previewEl = a;
  a.play().catch(() => { if (previewEl === a) { previewEl = null; previewUrl = null; tellPreview(0); } });
  tellPreview(startMs / 1000 / Math.max(1, a.duration || 1));
}
/** What is playing now and how far it is. */
function usePreviewing(): { url: string | null; progress: number } {
  const [st, setSt] = useState({ url: previewUrl, progress: 0 });
  useEffect(() => { const l = (url: string | null, progress: number) => setSt({ url, progress }); previewListeners.add(l); return () => { previewListeners.delete(l); }; }, []);
  return st;
}

/* ---------- the library's shapes and the owner's stars, fetched once per page ---------- */
type Peaks = { music: Record<string, SoundPeaks>; sfx: Record<string, SoundPeaks> };
let peaksCache: Peaks | null = null;
let peaksPending: Promise<Peaks> | null = null;
function useSoundPeaks(): Peaks | null {
  const [p, setP] = useState(peaksCache);
  useEffect(() => {
    if (peaksCache) { setP(peaksCache); return; }
    peaksPending ??= api.soundPeaks().then((r) => { peaksCache = r; return r; }).finally(() => { peaksPending = null; });
    let alive = true;
    peaksPending.then((r) => { if (alive) setP(r); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return p;
}
let favCache: string[] | null = null;
const favListeners = new Set<(ids: string[]) => void>();
function useFavourites(): [Set<string>, (id: string) => void] {
  const [ids, setIds] = useState<string[]>(favCache ?? []);
  useEffect(() => {
    favListeners.add(setIds);
    if (favCache) setIds(favCache); else api.soundFavourites().then((r) => { favCache = r.ids; favListeners.forEach((l) => l(r.ids)); }).catch(() => {});
    return () => { favListeners.delete(setIds); };
  }, []);
  const toggle = (id: string) => {
    const next = (favCache ?? ids).includes(id) ? (favCache ?? ids).filter((x) => x !== id) : [...(favCache ?? ids), id];
    favCache = next; favListeners.forEach((l) => l(next));
    api.setSoundFavourites(next).catch(() => {});
  };
  return [useMemo(() => new Set(ids), [ids]), toggle];
}
function useTracks(): [MusicTrack[], () => void] {
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const load = () => { api.music().then((m) => setTracks(m.tracks)).catch(() => {}); };
  useEffect(load, []);
  return [tracks, load];
}

/* ---------- the waveform: one path for the shape, one for the part already played ---------- */
const Wave: React.FC<{ peaks?: number[]; progress: number; height: number }> = ({ peaks, progress, height }) => {
  const n = peaks?.length ?? 48;
  const bars = peaks ?? Array.from({ length: n }, (_, i) => 0.25 + 0.15 * Math.abs(Math.sin(i * 0.7)));
  const seg = (from: number, to: number) => bars.slice(from, to).map((p, k) => { const i = from + k; const h = Math.max(1.2, p * 28); return `M${i + 0.5} ${16 - h / 2}v${h}`; }).join("");
  const played = Math.round(progress * n);
  return (
    <svg viewBox={`0 0 ${n} 32`} preserveAspectRatio="none" style={{ height }} aria-hidden="true">
      <path d={seg(0, n)} stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" fill="none" opacity={peaks ? 0.9 : 0.35} />
      {played > 0 ? <path className="played" d={seg(0, played)} stroke="currentColor" strokeWidth={0.7} strokeLinecap="round" fill="none" /> : null}
    </svg>
  );
};

const fmt = (ms: number) => { const s = Math.round(ms / 1000); return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : `${s}s`; };

/** One sound: its waveform (gold as far as it has played), a play button, a star, its name, where it is in use, and what it is. */
const SoundTile: React.FC<{ id: string; title: string; tag: string; url: string; peaks?: SoundPeaks; startMs: number; on: string | null; starred: boolean; height: number; onPick: () => void; onStar: () => void }> = ({ id, title, tag, url, peaks, startMs, on, starred, height, onPick, onStar }) => {
  const { url: playingUrl, progress } = usePreviewing();
  const playing = playingUrl === url;
  const hover = useRef<number | null>(null);
  const play = () => previewSound(playing ? null : url, startMs);
  // on a desk the pointer resting on a tile plays it; a finger never does (the play button is there for that)
  const enter = (e: React.PointerEvent) => { if (e.pointerType !== "mouse") return; hover.current = window.setTimeout(() => { hover.current = null; if (!playing) previewSound(url, startMs); }, HOVER_DELAY_MS); };
  const leave = (e: React.PointerEvent) => { if (e.pointerType !== "mouse") return; if (hover.current) { clearTimeout(hover.current); hover.current = null; } if (previewUrl === url) previewSound(null); };
  useEffect(() => () => { if (hover.current) clearTimeout(hover.current); }, []);
  return (
    <div className={`gtile sndtile${on ? " on" : ""}`} role="button" tabIndex={0} data-sound={id} aria-pressed={!!on} aria-label={`${title}${on ? `, in use: ${on}` : ""}`} onClick={onPick} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } }} onPointerEnter={enter} onPointerLeave={leave}>
      <div className="sndwave">
        <Wave peaks={peaks?.peaks} progress={playing ? progress : 0} height={height} />
        <button type="button" className={`sndstar${starred ? " on" : ""}`} aria-label={starred ? `Unstar ${title}` : `Star ${title}`} aria-pressed={starred} onClick={(e) => { e.stopPropagation(); onStar(); }}>{starred ? "★" : "☆"}</button>
        <button type="button" className="sndplay" aria-label={playing ? `Stop ${title}` : `Hear ${title}`} onClick={(e) => { e.stopPropagation(); play(); }}><Icon name={playing ? "pause" : "play"} size={16} /></button>
      </div>
      <span className="gtile-name">{title}{on ? <b>{on}</b> : null}</span>
      <span className="gtile-tags">{tag}{peaks ? ` · ${fmt(peaks.durationMs)}` : ""}</span>
    </div>
  );
};

/** Where an effect is in use on this clip, in words ("Opener · Every cut · 2 moments"), or null. */
export function effectUse(clip: Clip, id: string): string | null {
  const places: string[] = [];
  if (clip.sfx.hook && clip.sfx.openerId === id) places.push("Opener");
  if (clip.sfx.endCard && clip.sfx.endCardId === id) places.push("End card");
  if (clip.sfx.cuts && clip.sfx.cutsId === id) places.push("Every cut");
  const n = clip.sfx.moments.filter((m) => m.soundId === id).length;
  if (n) places.push(n === 1 ? "1 moment" : `${n} moments`);
  return places.length ? places.join(" · ") : null;
}

/** Puts an effect in one place (or takes it out again when it is already there): the opener, the end card, every cut, or a moment at the playhead. */
export function assignEffect(clip: Clip, id: string, target: SoundTarget, playheadSourceMs: number | null): { clip: Clip; note: string } | { error: string } {
  const sfx = clip.sfx;
  switch (target) {
    case "opener": { const same = sfx.hook && sfx.openerId === id; return { clip: { ...clip, sfx: { ...sfx, hook: !same, openerId: id } }, note: same ? "Opener sound off" : "On the opener" }; }
    case "endCard": { const same = sfx.endCard && sfx.endCardId === id; return { clip: { ...clip, sfx: { ...sfx, endCard: !same, endCardId: id } }, note: same ? "End card sound off" : "On the end card" }; }
    case "cuts": { const same = sfx.cuts && sfx.cutsId === id; return { clip: { ...clip, sfx: { ...sfx, cuts: !same, cutsId: id } }, note: same ? "Cut sound off" : "At every cut" }; }
    case "playhead": {
      if (playheadSourceMs == null) return { error: "Pause the preview inside the speech first" };
      const near = sfx.moments.find((m) => Math.abs(m.atMs - playheadSourceMs) < 120 && m.soundId === id);
      if (near) return { clip: { ...clip, sfx: { ...sfx, moments: sfx.moments.filter((m) => m.id !== near.id) } }, note: "Moment removed" };
      return { clip: { ...clip, sfx: { ...sfx, moments: [...sfx.moments, { id: sfxMomentId(), atMs: Math.round(playheadSourceMs), soundId: id, levelDb: 0 }].sort((a, b) => a.atMs - b.atMs) } }, note: "Placed at the playhead" };
    }
  }
}

/** Opens the library on one kind of sound, with a place for effects to go. */
let initialKind: Kind | null = null;
export function openSoundLibrary(kind: "all" | "beds" | "effects", target?: SoundTarget): void {
  initialKind = kind;
  const st = useEditor.getState();
  if (target) st.setSoundTarget(target);
  st.openGallery("sound");
}

/** The sound library: every bed and every effect as a tile with its waveform. Hover (desk) or the play button (phone) hears one; a tap uses it. */
export const SoundGallery: React.FC<{ columns?: number }> = ({ columns }) => {
  const clip = useEditor((s) => s.clip);
  const update = useEditor((s) => s.update);
  const target = useEditor((s) => s.soundTarget);
  const setTarget = useEditor((s) => s.setSoundTarget);
  const toast = useToast();
  const phone = usePhone();
  const sounds = useSfxLibrary();
  const [tracks, reloadTracks] = useTracks();
  const peaks = useSoundPeaks();
  const [stars, toggleStar] = useFavourites();
  const [kind, setKind] = useState<Kind>(() => { const k = initialKind ?? "all"; initialKind = null; return k; });
  const [sub, setSub] = useState<string>("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => () => previewSound(null), []); // leaving the library stops the sound
  const ref = useRef<HTMLDivElement>(null);
  const cols = columns ?? (phone ? 2 : 3);
  const waveH = phone ? 44 : 52;
  if (!clip) return null;
  const needle = q.trim().toLowerCase();
  const showBeds = kind === "all" || kind === "beds" || kind === "starred";
  const showFx = kind === "all" || kind === "effects" || kind === "starred";
  const beds = showBeds ? tracks.filter((t) => (kind !== "starred" || stars.has(t.file)) && (kind !== "beds" || sub === "all" || t.mood === sub) && (!needle || `${t.title} ${t.mood} ${MOOD_LABEL[t.mood] ?? ""} bed music nasheed`.toLowerCase().includes(needle))) : [];
  const fx = showFx ? sounds.filter((s) => (kind !== "starred" || stars.has(s.id)) && (kind !== "effects" || sub === "all" || s.group === sub) && (!needle || `${s.title} ${s.group} ${GROUP_LABEL[s.group] ?? ""} effect sfx`.toLowerCase().includes(needle))) : [];
  const currentBed = tracks.find((t) => t.file === clip.music.trackId) ?? null;
  const subs = kind === "beds" ? [...new Set(tracks.map((t) => t.mood))].map((m) => [m, MOOD_LABEL[m] ?? m]) : kind === "effects" ? [...new Set(sounds.map((s) => s.group))].map((g) => [g, GROUP_LABEL[g] ?? g]) : [];
  const pickBed = (t: MusicTrack) => {
    update((c) => ({ ...c, music: { ...c.music, trackId: t.file, muted: false } }));
    previewSound(`/assets/music/${t.file}`, peaks?.music[t.file]?.bestStartMs ?? 8000);
    toast(`Under the clip: ${t.title}`);
  };
  const pickFx = (s: SfxSound) => {
    const r = assignEffect(clip, s.id, target, useEditor.getState().playheadSourceMs());
    if ("error" in r) { toast(r.error); return; }
    update(() => r.clip);
    previewSound(`/assets/sfx/${s.file}`, 0);
    toast(`${s.title}: ${r.note}`);
  };
  const upload = async (f: File | undefined, what: "bed" | "effect") => {
    if (!f) return;
    setBusy(true);
    try {
      if (what === "bed") { const r = await api.uploadMusic(f); reloadTracks(); toast(`Added "${r.track.title}"`); }
      else { const r = await api.uploadSfx(f); await reloadSfx(); toast(`Added "${r.sound.title}"`); }
      peaksCache = null; // the new file's shape
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const levelPct = Math.round(((clip.music.volumeDb + 40) / 36) * 100);
  const fxCount = fx.length, bedCount = beds.length;
  return (
    <div className="gallery">
      <div className="gallery-head">
        <div className="gallery-search"><Icon name="search" size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sounds… calm, whoosh, hit" aria-label="Search sounds" /></div>
        <div className="gallery-cats" role="tablist" aria-label="Kind of sound">
          {([["all", "All"], ["beds", `Beds (${tracks.length})`], ["effects", `Effects (${sounds.length})`], ["starred", `★ Starred${stars.size ? ` (${stars.size})` : ""}`]] as [Kind, string][]).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? "on" : ""} onClick={() => { setKind(k); setSub("all"); }}>{label}</button>
          ))}
        </div>
        {subs.length ? <div className="gallery-cats" aria-label={kind === "beds" ? "Mood" : "Group"}>
          <button type="button" className={sub === "all" ? "on" : ""} onClick={() => setSub("all")}>All</button>
          {subs.map(([v, label]) => <button key={v} type="button" className={sub === v ? "on" : ""} onClick={() => setSub(v)}>{label}</button>)}
        </div> : null}
        {showFx ? <div className="gallery-targets" role="radiogroup" aria-label="Where a tapped effect goes">
          <span className="lbl" style={{ whiteSpace: "nowrap" }}>Effects go:</span>
          {SOUND_TARGETS.map((t) => <button key={t.value} type="button" role="radio" aria-checked={target === t.value} className={`chip${target === t.value ? " gold" : " solid"}`} style={{ minHeight: phone ? 44 : 32, padding: "0 12px" }} title={t.hint} onClick={() => setTarget(t.value)}>{t.label}</button>)}
        </div> : null}
        {phone ? null : <span className="hint">
          {kind === "effects" ? `Tap an effect to put it ${SOUND_TARGETS.find((t) => t.value === target)?.hint ?? ""}; tap again to take it off.` : kind === "beds" ? "Tap a bed to play it quietly under the whole clip, ducked while the speaker talks." : "Beds sit under the clip; effects go where the chips above say."}
          {" Rest on a tile to hear it."}
        </span>}
      </div>
      <div ref={ref} className="gallery-grid" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {showBeds && (kind === "all" || kind === "beds") ? <div className="row" style={{ gridColumn: "1 / -1", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span className="hint" style={{ fontWeight: 800, color: "var(--text-2)" }}>Beds</span>
          <button type="button" className={`chip${!clip.music.trackId ? " gold" : " solid"}`} style={{ minHeight: phone ? 44 : 30 }} onClick={() => { update((c) => ({ ...c, music: { ...c.music, trackId: null } })); toast("No BG audio"); }}>No BG audio</button>
          <label className="chip solid" style={{ minHeight: phone ? 44 : 30, cursor: "pointer" }}><Icon name="plus" size={12} />{busy ? "Adding…" : "Upload a bed"}<input type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0], "bed")} /></label>
        </div> : null}
        {beds.map((t) => (
          <SoundTile key={t.id} id={t.id} title={t.title} tag={MOOD_LABEL[t.mood] ?? t.mood} url={`/assets/music/${t.file}`} peaks={peaks?.music[t.file]} startMs={peaks?.music[t.file]?.bestStartMs ?? 8000} on={clip.music.trackId === t.file ? "In use" : null} starred={stars.has(t.file)} height={waveH} onPick={() => pickBed(t)} onStar={() => toggleStar(t.file)} />
        ))}
        {showFx && (kind === "all" || kind === "effects") ? <div className="row" style={{ gridColumn: "1 / -1", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: kind === "all" ? 6 : 0 }}>
          <span className="hint" style={{ fontWeight: 800, color: "var(--text-2)" }}>Effects</span>
          <label className="chip solid" style={{ minHeight: phone ? 44 : 30, cursor: "pointer" }}><Icon name="plus" size={12} />{busy ? "Adding…" : "Upload an effect"}<input type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0], "effect")} /></label>
        </div> : null}
        {fx.map((s) => (
          <SoundTile key={s.id} id={s.id} title={s.title} tag={GROUP_LABEL[s.group] ?? s.group} url={`/assets/sfx/${s.file}`} peaks={peaks?.sfx[s.id]} startMs={0} on={effectUse(clip, s.id)} starred={stars.has(s.id)} height={waveH} onPick={() => pickFx(s)} onStar={() => toggleStar(s.id)} />
        ))}
        {!bedCount && !fxCount ? <span className="hint" style={{ gridColumn: "1 / -1", padding: 20, textAlign: "center" }}>{kind === "starred" ? "No starred sounds yet: tap ☆ on any tile." : "No sound matches that."}</span> : null}
      </div>
      <div className="gallery-foot">
        {kind !== "effects" && currentBed ? <>
          <Slider label={`${currentBed.title} · level`} value={levelPct} min={0} max={100} format={(v) => `${v}%`} onChange={(v) => update((c) => ({ ...c, music: { ...c.music, volumeDb: Math.round(-40 + (v / 100) * 36) } }))} />
          {phone ? null : <span className="hint" style={{ marginTop: -6 }}>Quiet under the voice: most clips sit between 20 and 35%. Ducking, fades and the part of the track are in Sound → BG audio.</span>}
        </> : null}
        {kind === "effects" && target !== "playhead" ? <Slider label={`${SOUND_TARGETS.find((t) => t.value === target)?.label} sound · level`} value={clip.sfx.levels[target]} min={-18} max={12} format={(v) => (v === 0 ? "default" : `${v > 0 ? "+" : ""}${v} dB`)} onChange={(v) => update((c) => ({ ...c, sfx: { ...c.sfx, levels: { ...c.sfx.levels, [target]: v } } }))} /> : null}
        {kind === "effects" && target === "playhead" ? <span className="hint">{clip.sfx.moments.length ? `${clip.sfx.moments.length} placed moment${clip.sfx.moments.length === 1 ? "" : "s"} · listed in Sound → Sound effects, where each has its own level.` : "Pause the preview on a word, then tap an effect to place it there."}{clip.sfx.moments.length ? ` Clip: ${fmt(timelineDuration(clip.segments))}.` : ""}</span> : null}
      </div>
    </div>
  );
};

/** For the settings column: a placed moment's time on the timeline, in words. */
export const momentLabel = (clip: Clip, atMs: number): string => { const t = sfxMomentTimelineMs(clip.segments, { id: "", atMs, soundId: "", levelDb: 0 }); return t == null ? "in a cut" : `at ${(t / 1000).toFixed(1)}s`; };
